import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Users, HelpCircle, User, Monitor } from 'lucide-react';
import { Button } from './ui/button';

interface MeetingAttendanceButtonProps {
  meeting: {
    id: string;
    venue: 'Virtual' | 'In-Person' | 'Hybrid';
  };
  onAttend: (type: 'in-person' | 'virtual' | 'not-attending' | 'unconfirmed') => void;
  attendanceStatus?: 'in-person' | 'virtual' | 'not-attending';
  solidBackground: boolean;
}

const MeetingAttendanceButton: React.FC<MeetingAttendanceButtonProps> = ({
  meeting,
  onAttend,
  attendanceStatus,
  solidBackground
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);



  // Close popup on outside click
  useEffect(() => {
    if (!isModalOpen) return;
    function handleClick(event: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isModalOpen]);

  const getButtonText = () => {
    if (!attendanceStatus) return 'Attending?';
    if (attendanceStatus === 'not-attending') return 'Not Attending';
    if (meeting.venue === 'Hybrid') {
      if (attendanceStatus === 'virtual') return 'Attending (virtual)';
      if (attendanceStatus === 'in-person') return 'Attending (in person)';
    }
    return 'Attending';
  };

  const getButtonColorClass = (attendanceStatus?: 'in-person' | 'virtual' | 'not-attending') => {
    if (!attendanceStatus) {
      return 'border-yellow-500 text-yellow-700 bg-white hover:text-yellow-800 hover:border-yellow-600 hover:bg-yellow-50';
    }
    if (attendanceStatus === 'not-attending') {
      return 'border-red-600 bg-red-600 text-white hover:bg-red-700 hover:border-red-700';
    }
    // Solid green for both in-person and virtual
    return 'border-green-600 bg-green-600 text-white hover:bg-green-700 hover:border-green-700';
  };

  const handleOption = (type: 'in-person' | 'virtual' | 'not-attending') => {
    // If selecting the same option, revert to unconfirmed
    if (attendanceStatus === type) {
      onAttend('unconfirmed');
    } else {
      onAttend(type);
    }
    setIsModalOpen(false);
  };

  // Position popup above the button
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (isModalOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupStyle({
        position: 'fixed',
        left: rect.left,
        top: rect.top - 130, // Position above the button with some spacing
        zIndex: 1000
      });
    }
  }, [isModalOpen]);

  // Choose variant based on state
  const getButtonVariant = (attendanceStatus?: 'in-person' | 'virtual' | 'not-attending') => {
    if (attendanceStatus === 'in-person' || attendanceStatus === 'virtual' || attendanceStatus === 'not-attending') {
      return 'default'; // solid background
    }
    return 'outline'; // white background
  };

  return (
    <div className="relative inline-block">
      <span ref={buttonRef}>
        <Button
          variant={getButtonVariant(attendanceStatus)}
          size="sm"
          className={`flex items-center gap-2 h-10 px-3 text-sm whitespace-nowrap ${getButtonColorClass(attendanceStatus)}`}
          type="button"
          onClick={() => setIsModalOpen(v => !v)}
        >
          {attendanceStatus ? (
            attendanceStatus === 'not-attending' ? (
              <X className="h-4 w-4" />
            ) : attendanceStatus === 'virtual' ? (
              <Monitor className="h-4 w-4" />
            ) : (
              <User className="h-4 w-4" />
            )
          ) : (
            <HelpCircle className="h-4 w-4" />
          )}
          {getButtonText()}
        </Button>
      </span>
      {isModalOpen && (
        <div ref={popupRef} style={popupStyle} className="bg-white rounded-lg shadow-lg p-4 min-w-[220px] flex flex-col items-center border">
          <div className="mb-3 font-semibold text-gray-800">Select attendance option</div>
          {meeting.venue === 'Hybrid' ? (
            <>
              <button className="w-full mb-2 px-4 py-2 rounded flex items-center text-green-700 hover:bg-green-100 font-medium" onClick={() => handleOption('in-person')}>
                <User className="w-4 h-4 mr-2 text-green-500" /> Attending (in person)
              </button>
              <button className="w-full mb-2 px-4 py-2 rounded flex items-center text-green-700 hover:bg-green-100 font-medium" onClick={() => handleOption('virtual')}>
                <Monitor className="w-4 h-4 mr-2 text-green-500" /> Attending (virtual)
              </button>
            </>
          ) : (
            <button className="w-full mb-2 px-4 py-2 rounded flex items-center text-green-700 hover:bg-green-100 font-medium" onClick={() => handleOption('in-person')}>
              <User className="w-4 h-4 mr-2 text-green-500" /> Attending
            </button>
          )}
          <button className="w-full px-4 py-2 rounded flex items-center text-red-700 hover:bg-red-100 font-medium" onClick={() => handleOption('not-attending')}>
            <X className="w-4 h-4 mr-2 text-red-500" /> No
          </button>
        </div>
      )}
    </div>
  );
};

export default MeetingAttendanceButton; 